import { useId } from "react";
import type { PricingMethod, Prices } from "./labCatalog";

const boxClass =
  "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm tabular-nums text-slate-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-slate-100 disabled:text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";
const labelClass = "mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400";

const show = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));
const read = (s: string) => (s.trim() === "" ? null : Math.max(0, Number(s) || 0));

/**
 * Price boxes for one catalogue item: one for a flat item; base, per tooth
 * and teeth in the base for a base + per tooth item. Empty = not set.
 */
export default function LabPriceInputs({
  method,
  value,
  onChange,
  disabled = false,
  optional = true,
}: {
  method: PricingMethod;
  value: Prices;
  onChange: (next: Prices) => void;
  disabled?: boolean;
  optional?: boolean;
}) {
  const id = useId();
  const note = optional ? <span className="normal-case tracking-normal"> (optional)</span> : null;
  const box = (field: keyof Prices, label: string, opts: { integer?: boolean } = {}) => (
    <div className="min-w-0">
      <label htmlFor={`${id}-${field}`} className={labelClass}>
        {label}
        {field !== "includedTeeth" ? note : null}
      </label>
      <input
        id={`${id}-${field}`}
        type="number"
        min={0}
        step={opts.integer ? 1 : 0.01}
        inputMode="decimal"
        value={show(value[field])}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, [field]: read(e.target.value) })}
        className={boxClass}
      />
    </div>
  );
  if (method === "base_plus_tooth") {
    return (
      <div className="grid grid-cols-3 gap-3">
        {box("basePrice", "Base (LKR)")}
        {box("toothPrice", "Per tooth (LKR)")}
        {box("includedTeeth", "Teeth in base", { integer: true })}
      </div>
    );
  }
  return box("price", "Price (LKR)");
}
